import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Grid } from "@mui/material";
import { ToggleSurface } from "../core/Surface";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { nbNO, enUS } from "@mui/x-data-grid-pro/locales";
import { useJoinedEntities } from "@emberly/zenith-client";
import { useTranslation } from "react-i18next";
import { transpile } from "../../../common/filtertranspiler";
import { usePersistentFilter } from "../../../hooks/usePersistentFilter";
import { useStyles } from "./styles";
import { PAGESIZE, INITIAL_STATE, COMPONENTS, SX, autoHeight, EDITABLE_PROPERTY, GetFilterOperator } from "./constants";
import { useNavState } from "../../../providers/NavStateProvider";
import { useDevice } from "../../../providers/DeviceProvider";


const ONLY_AND = {
  logicOperators: ["and"]
};

export default function EntityJoinTable(props) {
  const { className, tableId, xs, sm, md, lg, xl, xxl, title, columns, type, emptyStateTitle, emptyStateDescription, filter, join, onRowClick, editable, processRowUpdate, disableRowHover, serverSidePaging, sortDescendingByDefault } = props;
  const { classes } = useStyles();
  const { i18n } = useTranslation();
  const containerRef = useRef(null);
  const { isMobile } = useDevice();

  const [paginationModel, setPaginationModel] = useState({
    pageSize: PAGESIZE,
    page: 0
  });

  const { page, pageSize } = paginationModel;
  const { registerPageState, stateEvents } = useNavState();

  const onChangePaginationModel = useCallback((paging) => {
    setPaginationModel(paging);
    registerPageState(`table:${tableId}`, paging);
    containerRef.current?.scrollIntoView();
  }, [registerPageState, tableId]);


  const resetPaging = useCallback(() => {
    onChangePaginationModel({ page: 0, pageSize });
  }, [onChangePaginationModel, pageSize]);

  const {
    sortModel,
    filterOverride,
    setSortModel,
    setFilterOverride,
    onReset,
    isPinned,
    visibleColumns,
    setVisibleColumns,
    pinnedColumns,
    setPinnedColumns,
    isCurrent
  } = usePersistentFilter(tableId, resetPaging);


  useEffect(() => {

    const fn = (pageState) => {
      if (pageState.page !== 0 || pageState.pageSize !== PAGESIZE) {
        setPaginationModel(pageState);
      }
    };

    stateEvents.on(`restorePageState:table:${tableId}`, fn);

    return () => {
      stateEvents.off(`restorePageState:table:${tableId}`, fn);
    }

  }, [stateEvents, tableId]);


  const sortByPath = sortModel.length === 1 ? sortModel[0].field : null;
  const sortDescending = sortModel.length === 1 ? sortModel[0].sort !== "asc" : sortDescendingByDefault;

  const { filterQuery, joinQuery } = useMemo(() => {
    if (serverSidePaging && !!filterOverride?.items?.length) {

      let splitBy = `${join.as}.`;
      let entityFilters = { ...filterOverride, items: filterOverride.items.filter(t => !t.field.startsWith(splitBy)) };
      let joinFilters = { ...filterOverride, items: filterOverride.items.filter(t => t.field.startsWith(splitBy)) };
      
      return {
        filterQuery: transpile(columns, entityFilters, filter),
        joinQuery: {
          ...join,
          filter: removePrefix(transpile(columns, joinFilters, join.filter), splitBy)
        }
      }
    } else {
      return { filterQuery: filter, joinQuery: join };
    }
  }, [filter, filterOverride, serverSidePaging, columns, join]);

  const { entities, loading, totalCount } = useJoinedEntities(type, filterQuery, joinQuery, isCurrent, serverSidePaging ? page : -1, pageSize, sortByPath, sortDescending);

  const renderColumns = useMemo(() => {
    if (!serverSidePaging) return columns;
    return columns.map(column => ({ ...column, filterOperators: GetFilterOperator(column) }));
  }, [columns, serverSidePaging]);

  const onHandleRowClick = useCallback((params, ev) => {
    if (window.getSelection()?.type !== "Range") {
      ev.preventDefault();
      if (!!onRowClick) {
        onRowClick(params, ev);
      }
    }
  }, [onRowClick]);

  const onSortModelChange = useCallback((sortModel) => {
    setSortModel(sortModel.length > 0 ? [sortModel[0]] : []);
  }, [setSortModel]);

  return (
    (<ToggleSurface
      xs={xs} sm={sm} md={md} lg={lg} xl={xl} xxl={xxl}
      outerClassName={className}
      title={title}
    >
      <Grid item xs={12} container>
        <div className={classes.gridInnerContainer} ref={containerRef}>
          <DataGridPro
            autoHeight
            disableColumnReorder
            sx={SX}
            className={`${classes.table} ${isMobile ? classes.tableMobile : ""} ${disableRowHover ? "" : classes.tableRowHover}`}
            rows={entities}
            loading={loading}
            columns={renderColumns}
            getRowHeight={autoHeight}
            onRowClick={onHandleRowClick}
            initialState={INITIAL_STATE}
            pageSizeOptions={useMemo(() => entities.length < PAGESIZE ? [Math.max(entities.length, 1), PAGESIZE, 50, 100, 150] : [PAGESIZE, 50, 100, 150], [entities.length])}
            disableRowSelectionOnClick

            paginationMode={serverSidePaging ? "server" : "client"}
            pagination
            paginationModel={paginationModel}
            onPaginationModelChange={onChangePaginationModel}

            filterModel={!!filterOverride ? filterOverride : undefined}
            filterMode={serverSidePaging ? "server" : "client"}
            onFilterModelChange={setFilterOverride}
            sortModel={sortModel}
            onSortModelChange={onSortModelChange}

            rowCount={serverSidePaging ? (entities.length < pageSize ? (pageSize * page + entities.length) : Math.max(totalCount, -1)) : undefined}
            columnVisibilityModel={visibleColumns}
            onColumnVisibilityModelChange={setVisibleColumns}
            pinnedColumns={pinnedColumns}
            onPinnedColumnsChange={setPinnedColumns}
            slots={COMPONENTS}
            slotProps={{
              filterPanel: ONLY_AND,
              noRowsOverlay: {
                title: emptyStateTitle,
                description: emptyStateDescription,
              },
              toolbar: {
                canSearch: !serverSidePaging,
                isPinned,
                tableId,
                onReset,
              }
            }}
            localeText={(i18n.language === "no" ? nbNO : enUS)?.components.MuiDataGrid.defaultProps.localeText}
            experimentalFeatures={editable ? EDITABLE_PROPERTY : undefined}
            processRowUpdate={processRowUpdate}
            onProcessRowUpdateError={console.log}
          />
        </div>
      </Grid>
    </ToggleSurface>)
  );
}




function removePrefix(filter, prefix) {
  try {
    if (filter === null) return null;

    if (!!filter.path?.startsWith(prefix)) {
      filter.path = filter.path.substring(prefix.length);
    }
  
    filter.or?.forEach(t => removePrefix(t, prefix));
    filter.and?.forEach(t => removePrefix(t, prefix));

  } catch (err) {
    console.log(err);
  }

  return filter;
}